if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        const errorTemplate = this.querySelector('template');

        this.errorHtml = errorTemplate?.content?.firstElementChild?.cloneNode(true) || null;
        this.abortController = null;

        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onClickOpenModal = this.onClickOpenModal.bind(this);

        this.fetchAvailability(this.dataset.variantId);
      }

      getDrawer() {
        return document.getElementById('PickupAvailabilityDrawer');
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        let rootUrl = this.dataset.rootUrl || '/';

        if (!rootUrl.endsWith('/')) {
          rootUrl += '/';
        }

        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        this.setAttribute('loading', '');

        fetch(variantSectionUrl, {
          signal: this.abortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Pickup availability request failed with status ${response.status}.`);
            }

            return response.text();
          })
          .then((text) => {
            const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
            const sectionInnerHTML = parsedDocument.querySelector('.shopify-section');

            if (!sectionInnerHTML) {
              throw new Error('Pickup availability section could not be found.');
            }

            this.renderPreview(sectionInnerHTML);
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;

            console.error('[Pickup availability]', error);
            this.renderError();
          })
          .finally(() => {
            this.removeAttribute('loading');
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      onClickOpenModal(event) {
        event.preventDefault();

        const drawer = this.getDrawer();

        if (!drawer || typeof drawer.show !== 'function') return;

        drawer.show(event.currentTarget);
      }

      update(variant) {
        if (variant?.available) {
          this.dataset.variantId = variant.id;
          this.fetchAvailability(variant.id);
          return;
        }

        this.abortController?.abort();
        this.removeAttribute('available');
        this.removeAttribute('loading');
        this.innerHTML = '';

        const drawer = this.getDrawer();

        if (drawer) {
          drawer.remove();
        }
      }

      renderError() {
        this.innerHTML = '';

        if (!this.errorHtml) {
          this.removeAttribute('available');
          return;
        }

        this.appendChild(this.errorHtml.cloneNode(true));

        const refreshButton = this.querySelector('button');

        if (refreshButton) {
          refreshButton.addEventListener('click', this.onClickRefreshList);
        }
      }

      renderPreview(sectionInnerHTML) {
        const preview = sectionInnerHTML.querySelector('pickup-availability-preview');
        const newDrawer = sectionInnerHTML.querySelector('pickup-availability-drawer');
        const existingDrawer = this.getDrawer();

        existingDrawer?.remove();

        if (!preview || !newDrawer) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');

        document.body.appendChild(newDrawer);

        const colourSchemeClasses = (this.dataset.productPageColorScheme || '')
          .split(' ')
          .filter(Boolean);

        colourSchemeClasses.forEach((colourClass) => {
          newDrawer.classList.add(colourClass);
        });

        const openButton = this.querySelector('#ShowPickupAvailabilityDrawer');

        if (openButton) {
          openButton.addEventListener('click', this.onClickOpenModal);
        }
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.modal = this.querySelector('.pickup-availability-modal');
        this.closeButton = this.querySelector('.pickup-availability-drawer-button');
        this.focusElement = null;
        this.scrollPosition = 0;

        this.onBackdropClick = this.handleBackdropClick.bind(this);
        this.onKeyUp = this.handleKeyUp.bind(this);
        this.onCloseClick = this.handleCloseClick.bind(this);

        this.addEventListener('click', this.onBackdropClick);
        this.addEventListener('keyup', this.onKeyUp);
        this.closeButton?.addEventListener('click', this.onCloseClick);
      }

      handleBackdropClick(event) {
        if (event.target === this) {
          this.hide();
        }
      }

      handleKeyUp(event) {
        if (event.code?.toUpperCase() === 'ESCAPE') {
          this.hide();
        }
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.hide();
      }

      lockPage() {
        this.scrollPosition = window.scrollY || document.documentElement.scrollTop || 0;

        document.documentElement.classList.add('pickup-modal-open');
        document.body.classList.add('pickup-modal-open');
        document.body.style.setProperty(
          '--pickup-modal-scroll-position',
          `-${this.scrollPosition}px`
        );
      }

      unlockPage() {
        document.documentElement.classList.remove('pickup-modal-open');
        document.body.classList.remove('pickup-modal-open');
        document.body.style.removeProperty('--pickup-modal-scroll-position');

        window.scrollTo(0, this.scrollPosition);
      }

      hide() {
        if (!this.hasAttribute('open')) return;

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        this.focusElement?.setAttribute('aria-expanded', 'false');

        this.unlockPage();

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.focusElement);
        } else {
          this.focusElement?.focus();
        }

        this.focusElement = null;
      }

      show(focusElement) {
        if (this.hasAttribute('open')) return;

        this.focusElement = focusElement;
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.focusElement?.setAttribute('aria-expanded', 'true');

        this.lockPage();

        if (typeof trapFocus === 'function') {
          trapFocus(this.modal || this, this.closeButton || this.modal || this);
        } else {
          (this.closeButton || this.modal || this).focus();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onBackdropClick);
        this.removeEventListener('keyup', this.onKeyUp);
        this.closeButton?.removeEventListener('click', this.onCloseClick);

        if (this.hasAttribute('open')) {
          this.unlockPage();
        }
      }
    }
  );
}
